import { createBrowserRouter, Navigate, useParams } from "react-router-dom";
import { lazy, type ReactElement } from "react";
import Loadable from "./components/Loadable";
import ProtectedRoute from "./components/ProtectedRoute";
import FreemiumProtectedRoute from "./components/FreemiumProtectedRoute";
import { Role } from './types/auth.type';
import { ADMIN_PANEL_ROLES, ALL_DASHBOARD_ROLES, EXPENSE_LIST_ROLES, INVOICE_PAGE_ROLES, ORG_CONTENT_ROLES, ORG_MANAGEMENT_PAGE_ROLES, REPORT_PAGE_ROLES, STAFF_TASK_ROLES, INTERNAL_CHAT_ROLES } from './utils/roles.utils';
import { getMohasbyAiAllowedRoles, canAccessMohasbyAI, canAccessWorkflow, canAccessInternalChat } from './config/featureFlags';
import { useAuthStore } from './store/auth.store';

const Login = Loadable(lazy(() => import("./pages/auth/login")));
const DemoEntry = Loadable(lazy(() => import("./pages/demo/DemoEntry")));
const NotFound = Loadable(lazy(() => import("./pages/error/notFound")));
const ReportsPeriod = Loadable(lazy(() => import("./pages/dashboard/reportsPeriod")));
const ReportDetails = Loadable(lazy(() => import("./pages/dashboard/ReportDetails")));
const DashboardLayout = Loadable(lazy(() => import("./pages/dashboard/DashboardLayout")));
const DashboardHomeRedirect = Loadable(lazy(() => import("./pages/dashboard/DashboardHomeRedirect")));
const AddOrganizations = Loadable(lazy(() => import("./pages/dashboard/addOrganizations")));
const AdminOrganizationEditPage = Loadable(lazy(() => import("./pages/dashboard/AdminOrganizationEditPage")));
const Organizations = Loadable(lazy(() => import("./pages/dashboard/organizations")));
const OrganizationInvoices = Loadable(lazy(() => import("./pages/dashboard/invoice")));
const AddInvoice = Loadable(lazy(() => import("./pages/dashboard/AddInvoice")));
const ExpenseListsPage = Loadable(lazy(() => import("./pages/dashboard/ExpenseListsPage")));
const ExpenseListDetails = Loadable(lazy(() => import("./pages/dashboard/ExpenseListDetails")));
const OrganizationReports = Loadable(lazy(() => import("./pages/dashboard/report")));
const AddReport = Loadable(lazy(() => import("./pages/dashboard/AddReport")));
const EditReport = Loadable(lazy(() => import("./pages/dashboard/EditReport")));
const OrganizationChat = Loadable(lazy(() => import("./pages/dashboard/chat")));
const MohаsbyAI = Loadable(lazy(() => import("./pages/dashboard/MohасbyAI")));
const Users = Loadable(lazy(() => import("./pages/dashboard/users")));
const AddUsers = Loadable(lazy(() => import("./pages/dashboard/addUsers")));
const UpdateUser = Loadable(lazy(() => import("./pages/dashboard/updateUsers")));
const ForgotPassword = Loadable(lazy(() => import("./pages/auth/forgotPassword")));
const ResetPassword = Loadable(lazy(() => import("./pages/auth/resetPassword")));
const NotAuthorized = Loadable(lazy(() => import("./pages/error/NotAuthorized")));
const Signup = Loadable(lazy(() => import("./pages/auth/signup")));
const BulkUpload = Loadable(lazy(() => import("./pages/dashboard/BulkUpload")));
const OverviewDashboard = Loadable(lazy(() => import("./pages/dashboard/OverviewDashboard")));
const GoogleCallback = Loadable(lazy(() => import("./pages/auth/GoogleCallback")));
const WafeqCallback = Loadable(lazy(() => import("./pages/auth/WafeqCallback")));
const ZohoCallback = Loadable(lazy(() => import("./pages/auth/ZohoCallback")));
const CreateTask = Loadable(lazy(() => import("./pages/dashboard/CreateTask")));
const AccountantOrganizationsPage = Loadable(lazy(() => import("./pages/dashboard/AccountantOrganizationsPage")));
const WorkflowPage = Loadable(lazy(() => import("./pages/dashboard/WorkflowPage")));
const EditTask = Loadable(lazy(() => import("./pages/dashboard/EditTask")));
const TaskDetails = Loadable(lazy(() => import("./pages/dashboard/TaskDetails")));
const OrganizationTasks = Loadable(lazy(() => import("./pages/dashboard/OrganizationTasks")));
const OrganizationAddOns = Loadable(lazy(() => import("./pages/dashboard/OrganizationAddOns")));
const AuditorContracting = Loadable(lazy(() => import("./pages/dashboard/AuditorContracting")));
const FinancialDashboardPage = Loadable(lazy(() => import("./pages/dashboard/FinancialDashboardPage")));
const AdminMonitoring = Loadable(lazy(() => import("./pages/dashboard/AdminMonitoring")));
const SubscriptionPlans = Loadable(lazy(() => import("./pages/dashboard/SubscriptionPlans")));
const SubscriptionSuccess = Loadable(lazy(() => import("./pages/dashboard/SubscriptionSuccess")));
const ContractSignPage = Loadable(lazy(() => import("./pages/dashboard/ContractSignPage")));
const ContractSuccessPage = Loadable(lazy(() => import("./pages/dashboard/ContractSuccessPage")));
const SubscriptionReturnRedirect = Loadable(lazy(() => import("./pages/subscription/SubscriptionReturnRedirect")));
const InviteUser = Loadable(lazy(() => import("./pages/dashboard/InviteUser")));
const AcceptInvitation = Loadable(lazy(() => import("./pages/auth/AcceptInvitation")));
const OrganizationManagementPage = Loadable(lazy(() => import("./pages/dashboard/OrganizationManagementPage")));
const SettingsPage = Loadable(lazy(() => import("./pages/dashboard/SettingsPage")));
const SectorOperationsDashboard = Loadable(lazy(() => import("./pages/dashboard/sectorOperations/SectorOperationsDashboard")));
const SectorOperationsMonitoring = Loadable(lazy(() => import("./pages/dashboard/sectorOperations/SectorOperationsMonitoring")));
const SectorOperationsProjectsPage = Loadable(lazy(() => import("./pages/dashboard/sectorOperations/SectorOperationsProjectsPage")));
const SectorOperationsAccountantOrganizationsPage = Loadable(lazy(() => import("./pages/dashboard/sectorOperations/SectorOperationsAccountantOrganizationsPage")));
const OrgUserLegacyRedirect = Loadable(lazy(() => import("./pages/dashboard/OrgLegacyRedirects").then((m) => ({ default: m.OrgUserLegacyRedirect }))));
const OrgInviteLegacyRedirect = Loadable(lazy(() => import("./pages/dashboard/OrgLegacyRedirects").then((m) => ({ default: m.OrgInviteLegacyRedirect }))));
const InternalChat = Loadable(lazy(() => import("./pages/dashboard/InternalChat")));
const InternalChatManagementPage = Loadable(lazy(() => import("./pages/dashboard/InternalChatManagementPage")));
const OrgManagementInternalChatPage = Loadable(lazy(() => import("./pages/dashboard/OrgManagementInternalChatPage")));

const MohasbyAiGate = ({ children }: { children: ReactElement }) => {
  const { user, isInitialized } = useAuthStore();
  const { organizationId } = useParams();
  if (!isInitialized) return <></>;
  if (!canAccessMohasbyAI(user?.data.role, user?.id, user?.email, organizationId)) return <Navigate to="/not-authorized" replace />;
  return children;
};

const WorkflowGate = ({ children }: { children: ReactElement }) => {
  const { user, isInitialized } = useAuthStore();
  if (!isInitialized) return <></>;
  if (!canAccessWorkflow(user?.data.role, user?.email)) return <Navigate to="/not-authorized" replace />;
  return children;
};

const InternalChatGate = ({ children }: { children: ReactElement }) => {
  const { user, isInitialized } = useAuthStore();
  if (!isInitialized) return <></>;
  if (!canAccessInternalChat(user?.data.role, user?.email)) return <Navigate to="/not-authorized" replace />;
  return children;
};

export const authRoutes = {
  path: "/",
  children: [
    {
      path: "",
      element: <Navigate to="/login" replace />,
    },
    {
      path: "login",
      element: <Login />,
    },
    {
      path: "signup",
      element: <Signup />,
    },
    {
      path: "forgot-password",
      element: <ForgotPassword />,
    },
    {
      path: "reset-password",
      element: <ResetPassword />,
    },
    {
      path: "auth/google/callback",
      element: (
        <ProtectedRoute requiredRoles={[Role.ACCOUNTANT, Role.CLEARING_OFFICER, Role.SECTOR_OPERATIONS_MANAGER, Role.ADMIN, Role.CUSTOMER_SUCCESS]}>
          <GoogleCallback />
        </ProtectedRoute>
      ),
    },
    {
      path: "auth/wafeq/callback",
      element: <WafeqCallback />,
    },
    {
      path: "auth/zoho/callback",
      element: <ZohoCallback />,
    },
    {
      path: "redeem-invite/:inviteCode",
      element: <AcceptInvitation />,
    },
    {
      path: "subscription/success",
      element: <SubscriptionReturnRedirect />,
    },
  ],
};

export const dashboardRoutes = {
  path: "/dashboard",
  element: (
    <ProtectedRoute
      requiredRoles={ALL_DASHBOARD_ROLES}
    >
      <DashboardLayout />
    </ProtectedRoute>
  ),
  children: [
    {
      index: true,
      element: <DashboardHomeRedirect />,
    },
    {
      path: "accountant-dashboard",
      element: (
        <ProtectedRoute
          requiredRoles={ORG_CONTENT_ROLES}
        >
          <OverviewDashboard />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/invoices",
      element: (
        <ProtectedRoute
          requiredRoles={INVOICE_PAGE_ROLES}
          checkOrganization={true}
          customPermission="access_invoices"
        >
          <OrganizationInvoices />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/invoices/add",
      element: (
        <ProtectedRoute
          requiredRoles={ORG_CONTENT_ROLES}
          checkOrganization={true}
          customPermission="access_invoices"
        >
          <AddInvoice />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/invoices/bulk",
      element: (
        <ProtectedRoute
          requiredRoles={ORG_CONTENT_ROLES}
          checkOrganization={true}
          customPermission="access_invoices"
        >
          <BulkUpload />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/expense-lists",
      element: (
        <ProtectedRoute
          requiredRoles={EXPENSE_LIST_ROLES}
          checkOrganization={true}
        >
          <ExpenseListsPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/expense-lists/:listId",
      element: (
        <ProtectedRoute
          requiredRoles={EXPENSE_LIST_ROLES}
          checkOrganization={true}
        >
          <ExpenseListDetails />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/reports-period",
      element: (
        <ProtectedRoute
          requiredRoles={REPORT_PAGE_ROLES}
          checkOrganization={true}
          customPermission="access_reports"
        >
          <ReportsPeriod />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/reports",
      element: (
        <ProtectedRoute
          requiredRoles={REPORT_PAGE_ROLES}
          checkOrganization={true}
          customPermission="access_reports"
        >
          <OrganizationReports />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/reports/add",
      element: (
        <ProtectedRoute
          requiredRoles={ORG_CONTENT_ROLES}
          checkOrganization={true}
          customPermission="access_reports"
        >
          <FreemiumProtectedRoute feature="add_reports">
            <AddReport />
          </FreemiumProtectedRoute>
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/reports/edit/:reportId",
      element: (
        <ProtectedRoute
          requiredRoles={ORG_CONTENT_ROLES}
          checkOrganization={true}
          customPermission="access_reports"
        >
          <FreemiumProtectedRoute feature="add_reports">
            <EditReport />
          </FreemiumProtectedRoute>
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/chat",
      element: (
        <ProtectedRoute
          requiredRoles={ORG_CONTENT_ROLES}
          checkOrganization={true}
          customPermission="access_chat"
        >
          <OrganizationChat />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/internal-chat",
      element: (
        <ProtectedRoute
          requiredRoles={INTERNAL_CHAT_ROLES}
          checkOrganization={true}
        >
          <InternalChatGate>
            <InternalChat />
          </InternalChatGate>
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/ai-chat",
      element: (
        <ProtectedRoute
          requiredRoles={getMohasbyAiAllowedRoles()}
          checkOrganization={true}
          customPermission="access_chat"
        >
          <MohasbyAiGate>
            <MohаsbyAI />
          </MohasbyAiGate>
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations",
      element: (
        <ProtectedRoute requiredRoles={[...ADMIN_PANEL_ROLES]}>
          <Organizations />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/add",
      element: (
        <ProtectedRoute requiredRoles={[...ADMIN_PANEL_ROLES]}>
          <AddOrganizations />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/edit",
      element: (
        <ProtectedRoute requiredRoles={[...ADMIN_PANEL_ROLES]}>
          <AdminOrganizationEditPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "users",
      element: (
        <ProtectedRoute requiredRoles={[...ADMIN_PANEL_ROLES]}>
          <Users />
        </ProtectedRoute>
      ),
    },
    {
      path: "users/add",
      element: (
        <ProtectedRoute requiredRoles={[...ADMIN_PANEL_ROLES]}>
          <AddUsers />
        </ProtectedRoute>
      ),
    },
    {
      path: "users/:id/update",
      element: (
        <ProtectedRoute requiredRoles={[...ADMIN_PANEL_ROLES]}>
          <UpdateUser />
        </ProtectedRoute>
      ),
    },
    {
      path: "monitoring",
      element: (
        <ProtectedRoute requiredRoles={[...ADMIN_PANEL_ROLES]}>
          <AdminMonitoring />
        </ProtectedRoute>
      ),
    },
    {
      path: "internal-chat",
      element: (
        <InternalChatGate>
          <InternalChatManagementPage />
        </InternalChatGate>
      ),
    },
    {
      path: "internal-chat/groups/:groupId",
      element: (
        <InternalChatGate>
          <InternalChatManagementPage />
        </InternalChatGate>
      ),
    },
    {
      path: "internal-chat/:organizationId",
      element: (
        <InternalChatGate>
          <InternalChatManagementPage />
        </InternalChatGate>
      ),
    },
    {
      path: "admin/plans",
      element: (
        <ProtectedRoute requiredRoles={[...ADMIN_PANEL_ROLES]}>
          <Navigate to="/dashboard/organizations?tab=plans" replace />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/subscription/plans",
      element: (
        <ProtectedRoute
          requiredRoles={[Role.ORGANIZATION]}
          checkOrganization={true}
        >
          <SubscriptionPlans />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/subscription/success",
      element: (
        <ProtectedRoute
          requiredRoles={[Role.ORGANIZATION]}
          checkOrganization={true}
        >
          <SubscriptionSuccess />
        </ProtectedRoute>
      ),
    },
    {
      path: "contracts/:contractId/sign",
      element: (
        <ProtectedRoute requiredRoles={[Role.ORGANIZATION, Role.ADMIN]}>
          <ContractSignPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "contracts/:contractId/success",
      element: (
        <ProtectedRoute requiredRoles={[Role.ORGANIZATION, Role.ADMIN]}>
          <ContractSuccessPage />
        </ProtectedRoute>
      ),
    },
    {
      path: '/dashboard/organizations/:organizationId/reports/:reportId',
      element: (
        <ProtectedRoute
          requiredRoles={REPORT_PAGE_ROLES}
          checkOrganization={true}
          customPermission="access_reports"
        >
          <ReportDetails />
        </ProtectedRoute>
      )
    },
    {
      path: "tasks",
      element: (
        <ProtectedRoute requiredRoles={ORG_MANAGEMENT_PAGE_ROLES}>
          <AccountantOrganizationsPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "org-internal-chat",
      element: (
        <InternalChatGate>
          <OrgManagementInternalChatPage />
        </InternalChatGate>
      ),
    },
    {
      path: 'sector-operations/dashboard',
      element: (
        <ProtectedRoute requiredRoles={[Role.SECTOR_OPERATIONS_MANAGER]}>
          <SectorOperationsDashboard />
        </ProtectedRoute>
      ),
    },
    {
      path: 'sector-operations/monitoring',
      element: (
        <ProtectedRoute requiredRoles={[Role.SECTOR_OPERATIONS_MANAGER]}>
          <SectorOperationsMonitoring />
        </ProtectedRoute>
      ),
    },
    {
      path: 'sector-operations/projects',
      element: (
        <ProtectedRoute requiredRoles={[Role.SECTOR_OPERATIONS_MANAGER]}>
          <SectorOperationsProjectsPage />
        </ProtectedRoute>
      ),
    },
    {
      path: 'sector-operations/accountants/:accountantId/organizations',
      element: (
        <ProtectedRoute requiredRoles={[Role.SECTOR_OPERATIONS_MANAGER]}>
          <SectorOperationsAccountantOrganizationsPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "workflow",
      element: (
        <WorkflowGate>
          <WorkflowPage />
        </WorkflowGate>
      ),
    },
    {
      path: "tasks/create",
      element: (
        <ProtectedRoute requiredRoles={STAFF_TASK_ROLES}>
          <CreateTask />
        </ProtectedRoute>
      ),
    },
    {
      path: "tasks/:taskId",
      element: (
        <ProtectedRoute requiredRoles={STAFF_TASK_ROLES}>
          <TaskDetails />
        </ProtectedRoute>
      ),
    },
    {
      path: "tasks/:taskId/edit",
      element: (
        <ProtectedRoute requiredRoles={STAFF_TASK_ROLES}>
          <EditTask />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/tasks",
      element: (
        <ProtectedRoute
          requiredRoles={ORG_CONTENT_ROLES}
          checkOrganization={true}
        >
          <OrganizationTasks />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/addons",
      element: (
        <ProtectedRoute
          requiredRoles={ORG_CONTENT_ROLES}
          checkOrganization={true}
        >
          <OrganizationAddOns />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/auditor-contracting",
      element: (
        <ProtectedRoute
          requiredRoles={[Role.ADMIN, Role.CUSTOMER_SUCCESS, Role.ACCOUNTANT, Role.CLEARING_OFFICER, Role.SALES, Role.ORGANIZATION, Role.ORGANIZATION_USER]}
          checkOrganization={true}
        >
          <AuditorContracting />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/financial-dashboard",
      element: (
        <ProtectedRoute
          requiredRoles={ORG_CONTENT_ROLES}
          checkOrganization={true}
        >
          <FinancialDashboardPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/invite-user",
      element: (
        <ProtectedRoute
          requiredRoles={[Role.ADMIN, Role.CUSTOMER_SUCCESS, Role.ORGANIZATION]}
          checkOrganization={true}
        >
          <InviteUser />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/organization",
      element: (
        <ProtectedRoute
          requiredRoles={[Role.ADMIN, Role.CUSTOMER_SUCCESS, Role.ORGANIZATION]}
          checkOrganization={true}
        >
          <OrganizationManagementPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/settings",
      element: (
        <ProtectedRoute
          requiredRoles={[Role.ADMIN, Role.CUSTOMER_SUCCESS, Role.ORGANIZATION]}
          checkOrganization={true}
        >
          <SettingsPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "organizations/:organizationId/users/:userId",
      element: <OrgUserLegacyRedirect />,
    },
    {
      path: "organizations/:organizationId/users/invite",
      element: <OrgInviteLegacyRedirect />,
    },
  ],
};



export const router = createBrowserRouter([
  {
    path: "/",
    element: <Navigate to="/login" replace />,
  },
  authRoutes,
  dashboardRoutes,
  {
    // Public marketing demo entry point (no auth) — seeds a fake session and
    // redirects into the fully-faked organization view.
    path: "demo",
    element: <DemoEntry />,
  },
  {
    path: "not-authorized",
    element: <NotAuthorized />,
  },
  { path: "*", element: <NotFound /> },
]);
